import type React from 'react';
import { Switch as MuiSwitch, type SwitchProps } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';

/**
 * iOS-style Switch component with theme-aware colors
 * Maintains iOS dimensions by default but supports MUI size prop
 */
export const StyledSwitch: React.ComponentType<SwitchProps> = styled(MuiSwitch)<SwitchProps>(
  ({ theme, size }: { theme: Theme; size?: 'small' | 'medium' | 'large' }) => {
    // Calculate dimensions based on size prop
    const dimensions = {
      small: { width: 42, height: 21, translateX: 21, thumbSize: 19 },
      medium: { width: 51, height: 26, translateX: 25, thumbSize: 24 },
      large: { width: 60, height: 31, translateX: 29, thumbSize: 29 },
    };

    const currentSize = dimensions[size || 'medium'];

    return {
      width: currentSize.width,
      height: currentSize.height,
      padding: 0,
      '& .MuiSwitch-switchBase': {
        padding: 0,
        margin: 1,
        transitionDuration: '300ms',
        '&.Mui-checked': {
          transform: `translateX(${currentSize.translateX}px)`,
          color: '#fff',
          '& .MuiSwitch-thumb': {
            borderColor: theme.palette.primary.main,
          },
          '& + .MuiSwitch-track': {
            backgroundColor: theme.palette.primary.main,
            opacity: 1,
            border: `1px solid ${theme.palette.primary.main}`,
          },
          '&.Mui-disabled + .MuiSwitch-track': {
            opacity: 0.5,
          },
        },
        '&.Mui-focusVisible .MuiSwitch-thumb': {
          color: theme.palette.primary.main,
          border: '6px solid #fff',
        },
        '&.Mui-disabled .MuiSwitch-thumb': {
          color: theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[600],
        },
        '&.Mui-disabled + .MuiSwitch-track': {
          opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
        },
      },
      '& .MuiSwitch-thumb': {
        boxSizing: 'border-box',
        width: currentSize.thumbSize,
        height: currentSize.thumbSize,
        border: '1px solid rgba(0, 13, 77, 0.45)',
        boxShadow: 'none',
        transition: theme.transitions.create(['border-color'], { duration: 300 }),
      },
      '& .MuiSwitch-track': {
        borderRadius: currentSize.height / 2,
        backgroundColor: '#E9E9EA',
        opacity: 1,
        border: '1px solid rgba(0, 13, 77, 0.45)',
        transition: theme.transitions.create(['background-color', 'border-color'], {
          duration: 500,
        }),
      },
    };
  }
);